// Gider Fişi sayfası — faturasız gider girişi (Faz 1).
// Belge tipleri: gider fişi, gider pusulası (stopaj), KKEG.
// Ödeme aracı: kasa / banka / kredi kartı / cariye borç / personel avansı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import DataTable from '../components/DataTable';
import SearchableSelect from '../components/SearchableSelect';
import { fmtAmount } from '../utils/numberFormat';
import { formatCariOption } from '../utils/cariFormat';
import { TAB_ICONS } from '../utils/tabIcons';

const DOC_TYPES = [
  { value: 'gider_fisi',     label: 'Gider Fişi / Makbuz', desc: 'Standart faturasız gider (otopark, taksi, fiş…)' },
  { value: 'gider_pusulasi', label: 'Gider Pusulası',      desc: 'Esnaftan/şahıstan alış — stopaj kesilir' },
  { value: 'belgesiz_kkeg',  label: 'Belgesiz / KKEG',     desc: 'Kanunen kabul edilmeyen gider (KV matrahına eklenecek)' },
];

const PAY_METHODS = [
  { value: 'cash',      label: 'Kasa',           color: '#16a34a' },
  { value: 'bank',      label: 'Banka',          color: '#3b82f6' },
  { value: 'card',      label: 'Kredi Kartı',    color: '#dc2626' },
  { value: 'cari',      label: 'Cariye Borç',    color: '#7c3aed' },
  { value: 'personnel', label: 'Personel Avansı', color: '#d97706' },
];

const VAT_RATES = [0, 1, 10, 20];

const fmt = (n) => fmtAmount(n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';
const today = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({ category_id: '', description: '', net_amount: '', vat_rate: 20, withholding_rate: 0 });
const emptyHeader = () => ({
  voucher_date: today(), document_type: 'gider_fisi',
  account_id: '', description: '', reference_no: '',
  payment_method: 'cash', payment_ref_id: '',
  currency_code: 'TRY', notes: '',
});

export default function ExpenseVouchersPage({ autoOpenId } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [categories, setCategories] = useState([]);
  const [instruments, setInstruments] = useState({ cash: [], bank: [], card: [] });
  const [accounts, setAccounts] = useState([]);
  const [personnel, setPersonnel] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('add'); // 'add' | 'view' | 'edit'
  const [selected, setSelected] = useState(null);
  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/expense-vouchers', { params: { search } });
      if (r.data.success) setList(r.data.data.items);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search]);

  const fetchLookups = useCallback(async () => {
    try {
      const [c, i, a, p] = await Promise.all([
        api.get('/expense-vouchers/categories'),
        api.get('/expense-vouchers/lookup/payment-instruments'),
        api.get('/accounts', { params: { limit: 500 } }),
        api.get('/personnel', { params: { limit: 200 } }).catch(() => ({ data: { success: false } })),
      ]);
      if (c.data.success) setCategories(c.data.data.filter(x => x.is_active));
      if (i.data.success) setInstruments(i.data.data);
      if (a.data.success) setAccounts((a.data.data.items || []).filter(x => x.is_active));
      if (p.data.success) setPersonnel((p.data.data.items || []).filter(x => x.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchList(); fetchLookups(); }, [fetchList, fetchLookups]);

  useEffect(() => {
    if (autoOpenId) openView(autoOpenId);
  }, [autoOpenId]); // eslint-disable-line

  // Toplam hesaplama (canlı)
  const totals = (() => {
    let net = 0, vat = 0, wh = 0;
    for (const l of lines) {
      const n = parseFloat(l.net_amount) || 0;
      const vr = parseFloat(l.vat_rate) || 0;
      const wr = parseFloat(l.withholding_rate) || 0;
      const v = n * vr / 100;
      net += n; vat += v; wh += (n + v) * wr / 100;
    }
    return { net, vat, wh, gross: net + vat, payable: net + vat - wh };
  })();

  const openAdd = () => {
    if (categories.length === 0) {
      alert('Önce sol menüdeki "Gider Masraf Kartı" sayfasından kategori tanımlayın.');
      return;
    }
    setMode('add');
    setSelected(null);
    setHeader(emptyHeader());
    setLines([emptyLine()]);
    setMsg(null);
    setShowModal(true);
  };

  const openView = async (id) => {
    try {
      const r = await api.get('/expense-vouchers/' + id);
      if (r.data.success) {
        setMode('view');
        setSelected(r.data.data);
        setShowModal(true);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Yüklenemedi' }); }
  };

  const openEdit = () => {
    if (!selected) return;
    setHeader({
      voucher_date: selected.voucher_date ? selected.voucher_date.slice(0, 10) : today(),
      document_type: selected.document_type || 'gider_fisi',
      account_id: selected.account_id || '',
      description: selected.description || '',
      reference_no: selected.reference_no || '',
      payment_method: selected.payment_method || 'cash',
      payment_ref_id: selected.payment_ref_id || '',
      currency_code: selected.currency_code || 'TRY',
      exchange_rate: selected.exchange_rate || 1,
      notes: selected.notes || '',
    });
    const ls = (selected.lines || []).map(l => ({
      category_id: l.category_id || '',
      description: l.description || '',
      net_amount: l.net_amount,
      vat_rate: l.vat_rate,
      withholding_rate: l.withholding_rate || 0,
    }));
    setLines(ls.length ? ls : [emptyLine()]);
    setMsg(null);
    setMode('edit');
  };

  const updateLine = (idx, key, val) => {
    setLines(prev => {
      const nl = [...prev];
      nl[idx] = { ...nl[idx], [key]: val };
      // Kategori değiştiğinde default KDV oranını uygula
      if (key === 'category_id') {
        const cat = categories.find(c => c.id === val);
        if (cat && cat.default_vat_rate != null) nl[idx].vat_rate = cat.default_vat_rate;
        // Pusula seçili ise kategori değişiminde stopajı koru; aksi halde stopajsız
      }
      return nl;
    });
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

  const save = async (saveMode = 'close') => {
    if (saveMode && typeof saveMode === 'object') saveMode = 'close';
    setMsg(null);
    if (!header.voucher_date) return setMsg({ type: 'error', text: 'Tarih zorunlu' });
    if (!header.payment_ref_id && header.payment_method !== 'personnel') {
      return setMsg({ type: 'error', text: 'Ödeme aracı seçilmelidir' });
    }
    if (header.payment_method === 'cari' && !header.payment_ref_id) {
      return setMsg({ type: 'error', text: 'Cari seçilmelidir' });
    }
    if (header.payment_method === 'personnel' && !header.payment_ref_id) {
      return setMsg({ type: 'error', text: 'Personel seçilmelidir' });
    }
    const validLines = lines.filter(l => l.category_id && parseFloat(l.net_amount) > 0);
    if (validLines.length === 0) return setMsg({ type: 'error', text: 'En az bir geçerli kalem girin (kategori + tutar)' });

    setSaving(true);
    try {
      const payload = {
        ...header,
        lines: validLines.map(l => ({
          category_id: l.category_id,
          description: l.description || null,
          net_amount: parseFloat(l.net_amount) || 0,
          vat_rate: parseFloat(l.vat_rate) || 0,
          withholding_rate: parseFloat(l.withholding_rate) || 0,
        })),
      };
      const r = mode === 'edit' && selected
        ? await api.put('/expense-vouchers/' + selected.id, payload)
        : await api.post('/expense-vouchers', payload);
      if (r.data.success) {
        setMsg({ type: 'success', text: mode === 'edit' ? `Gider fişi güncellendi: ${r.data.data.voucher_no}` : `Gider fişi kaydedildi: ${r.data.data.voucher_no}` });
        await fetchList();
        await fetchLookups();
        if (mode === 'edit') {
          setTimeout(() => { setShowModal(false); setMsg(null); }, 1000);
          setSaving(false);
          return;
        }
        if (saveMode === 'new') {
          // Modal açık kalsın, formu sıfırla
          setTimeout(() => {
            setHeader({ ...header, payment_ref_id: '', description: '' });
            setLines([]);
            setMsg({ type: 'success', text: 'Yeni gider fişi için form sıfırlandı' });
            setTimeout(() => setMsg(null), 2000);
          }, 800);
        } else {
          setTimeout(() => { setShowModal(false); setMsg(null); }, 1200);
        }
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setSaving(false); }
  };

  const cancelVoucher = async (id) => {
    if (!window.confirm('Bu gider fişi kalıcı olarak silinecek. Yevmiye ve ödeme hareketi geri alınacak. Devam edilsin mi?')) return;
    try {
      await api.delete('/expense-vouchers/' + id);
      setShowModal(false);
      fetchList();
      fetchLookups();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    }
  };

  // Fiş para birimi ile ödeme aracını hizala — sadece aynı para birimindeki araçları göster
  const cur = header.currency_code || 'TRY';
  const payInstrumentOptions = () => {
    if (header.payment_method === 'cash')      return instruments.cash.filter(x => (x.currency_code||'TRY') === cur).map(x => ({ id: x.id, name: x.name, currency: x.currency_code, balance: x.current_balance }));
    if (header.payment_method === 'bank')      return instruments.bank.filter(x => (x.currency_code||'TRY') === cur).map(x => ({ id: x.id, name: x.name, currency: x.currency_code, balance: x.current_balance }));
    if (header.payment_method === 'card')      return instruments.card.filter(x => (x.currency_code||'TRY') === cur).map(x => ({ id: x.id, name: x.name, currency: x.currency_code, balance: x.current_balance, isCard: true }));
    if (header.payment_method === 'cari')      return accounts.map(a => ({ id: a.id, name: a.title, code: a.code, currency: a.currency_code, ...a }));
    if (header.payment_method === 'personnel') return personnel.map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name}` }));
    return [];
  };

  const payInstrumentLabel = () => {
    if (header.payment_method === 'cari') return 'Cari (Tedarikçi)';
    if (header.payment_method === 'personnel') return 'Personel';
    return PAY_METHODS.find(p => p.value === header.payment_method)?.label || 'Hesap';
  };

  const docTypeBadge = (t) => {
    const d = DOC_TYPES.find(x => x.value === t);
    return d ? d.label : t;
  };
  const payMethodBadge = (m) => {
    const p = PAY_METHODS.find(x => x.value === m);
    if (!p) return m;
    return <span style={{ color: p.color, fontWeight: 600 }}>{p.label}</span>;
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Gider Fişleri"
        count={list.length}
        unit="fiş"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Fiş no, belge no, açıklama, cari ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Gider Fişi</Button>}
      />

      {categories.length === 0 && !loading && (
        <div style={{
          padding: '12px 14px', margin: '12px 0', borderRadius: 8, fontSize: '0.85rem',
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
          color: 'var(--warning)',
        }}>
          <strong>Henüz gider kategorisi yok.</strong>
          <span style={{ marginLeft: 6, opacity: 0.85 }}>Sol menüden "Gider Masraf Kartı" sayfasından kategori tanımlayın veya varsayılanları yükleyin.</span>
        </div>
      )}

      <div className="stocks-table-wrap">
      <DataTable
        pageKey="expense-vouchers-list"
        tableClassName="stocks-table"
        rowClassName="stocks-table-row"
        loading={loading}
        data={list}
        emptyText="Gider fişi bulunamadı"
        rowKey="id"
        onRowClick={r => openView(r.id)}
        columns={[
          { id: 'voucher_no', header: 'Fiş No', accessor: 'voucher_no', sortable: true, width: 160,
            cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.voucher_no}</span> },
          { id: 'voucher_date', header: 'Tarih', accessor: 'voucher_date', sortable: true, width: 100,
            cell: r => fmtDate(r.voucher_date) },
          { id: 'document_type', header: 'Tip', accessor: 'document_type', sortable: true, width: 170,
            cell: r => <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{docTypeBadge(r.document_type)}</span> },
          { id: 'reference_no', header: 'Belge No', accessor: 'reference_no', sortable: true, width: 130,
            cell: r => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{r.reference_no || '-'}</span> },
          { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
            cell: r => (
              <div>
                {r.lines_summary && (
                  <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>{r.lines_summary}</div>
                )}
                {r.description && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{r.description}</div>
                )}
                {!r.lines_summary && !r.description && (r.account_title || '-')}
              </div>
            ) },
          { id: 'total_net', header: 'Net', accessor: 'total_net', sortable: true, width: 110,
            cell: r => <span style={{ fontFamily: 'monospace' }}>{fmt(r.total_net)}</span> },
          { id: 'total_vat', header: 'KDV', accessor: 'total_vat', sortable: true, width: 110,
            cell: r => <span style={{ fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{fmt(r.total_vat)}</span> },
          { id: 'total_gross', header: 'Toplam', accessor: 'total_gross', sortable: true, width: 140,
            cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--danger)' }}>{fmt(r.total_gross)} {r.currency_code}</span> },
          { id: 'payment_method', header: 'Ödeme', accessor: 'payment_method', sortable: true, width: 170,
            cell: r => (
              <div>
                {payMethodBadge(r.payment_method)}
                {r.payment_ref_name && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{r.payment_ref_name}</div>
                )}
              </div>
            ) },
          { id: 'status', header: 'Durum', accessor: 'status', sortable: true, width: 80,
            cell: r => r.status === 'cancelled'
              ? <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>İptal</span>
              : <span style={{ color: 'var(--success)', fontSize: '0.78rem' }}>Aktif</span> },
        ]}
      />
      </div>

      {showModal && (
        <Modal
          icon={null}
          title={mode === 'add' ? 'Yeni Gider Fişi' : mode === 'edit' ? 'Gider Fişi Düzenle' : 'Gider Fişi'}
          size="xl"
          onClose={() => setShowModal(false)}
          closeOnOverlay={false}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {mode === 'view' && selected && (
                  <Button variant="danger" onClick={() => cancelVoucher(selected.id)}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {mode === 'add' && <Button variant="ghost" onClick={() => setShowModal(false)}>Vazgeç</Button>}
                {mode === 'edit' && <Button variant="ghost" onClick={() => setMode('view')}>Vazgeç</Button>}
                {mode === 'view' && selected && selected.status !== 'cancelled' && (
                  <Button variant="primary" onClick={openEdit}>✎ Düzenle</Button>
                )}
                {mode === 'add' && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => save('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => save('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => save('close') },
                    ]}
                  />
                )}
                {mode === 'edit' && (
                  <Button variant="primary" onClick={() => save('close')} loading={saving}>💾 Güncelle</Button>
                )}
              </div>
            </>
          }
        >
          {/* Hero strip — view modunda */}
          {mode === 'view' && selected && (
            <CardHero
              icon="💸"
              code={selected.voucher_no}
              title={docTypeBadge(selected.document_type)}
              subtitle={fmtDate(selected.voucher_date)}
              statusBadge={
                <CardStatusBadge
                  preset={selected.status === 'cancelled' ? 'cancelled' : 'approved'}
                />
              }
              kpis={[
                {
                  label: 'Toplam Tutar',
                  value: fmt(selected.total_gross),
                  color: 'danger',
                  sublabel: selected.currency_code,
                },
                selected.total_vat > 0 && { label: 'KDV', value: fmt(selected.total_vat), color: 'neutral' },
                selected.total_net != null && { label: 'Net', value: fmt(selected.total_net), color: 'info' },
              ].filter(Boolean)}
            />
          )}

          {msg && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, borderRadius: 8, fontSize: '0.85rem',
              background: msg.type === 'error' ? 'rgba(220,38,38,0.1)' : 'rgba(22,163,74,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : 'var(--success)',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'),
            }}>{msg.text}</div>
          )}

          {mode === 'add' || mode === 'edit' ? (
            <AddForm
              header={header} setHeader={setHeader}
              lines={lines} updateLine={updateLine} addLine={addLine} removeLine={removeLine}
              categories={categories}
              payInstrumentOptions={payInstrumentOptions}
              payInstrumentLabel={payInstrumentLabel}
              accounts={accounts}
              totals={totals}
            />
          ) : (
            selected && <ViewForm voucher={selected} categories={categories} />
          )}
        </Modal>
      )}

    </div>
  );
}

// ─── Add Form ────────────────────────────────────────────────────────

function AddForm({ header, setHeader, lines, updateLine, addLine, removeLine, categories, payInstrumentOptions, payInstrumentLabel, accounts, totals }) {
  const h = (k, v) => setHeader(prev => ({ ...prev, [k]: v }));
  const isPusula = header.document_type === 'gider_pusulasi';
  const cur = header.currency_code || 'TRY';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Header alanları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <FormField label="Tarih *">
          <input type="date" value={header.voucher_date} onChange={e => h('voucher_date', e.target.value)} />
        </FormField>
        <FormField label="Belge Türü *">
          <select value={header.document_type} onChange={e => h('document_type', e.target.value)}>
            {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </FormField>
        <FormField label="Para Birimi *">
          <select value={header.currency_code || 'TRY'}
            onChange={e => setHeader(prev => ({ ...prev, currency_code: e.target.value, payment_ref_id: '' }))}>
            {['TRY','USD','EUR','GBP','CHF','SAR','AED'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Belge No (fiş/makbuz üzerindeki numara)" hint="Fiş üstündeki seri no — ör: A-12345">
          <input value={header.reference_no} onChange={e => h('reference_no', e.target.value)} placeholder="opsiyonel" />
        </FormField>
        <FormField label="Tedarikçi / Cari (ops.)">
          <SearchableSelect
            value={header.account_id}
            onChange={v => h('account_id', v)}
            options={accounts}
            getValue={a => a.id}
            getLabel={formatCariOption}
            placeholder="Cari ara… (boş bırakılabilir)"
            allowClear
          />
        </FormField>
      </div>

      <FormField label="Açıklama">
        <input value={header.description} onChange={e => h('description', e.target.value)} placeholder="Bu giderin kısa açıklaması…" />
      </FormField>

      <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
        💡 {DOC_TYPES.find(d => d.value === header.document_type)?.desc}
      </div>

      {/* Kalemler */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Kalemler</h4>
          <Button size="sm" variant="ghost" onClick={addLine}>+ Kalem Ekle</Button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                <th style={{ textAlign: 'left', padding: '6px 4px', width: 220 }}>Kategori *</th>
                <th style={{ textAlign: 'left', padding: '6px 4px' }}>Açıklama</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 110 }}>Net Tutar *</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 70 }}>KDV %</th>
                {isPusula && <th style={{ textAlign: 'right', padding: '6px 4px', width: 90 }}>Stopaj %</th>}
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 110 }}>Toplam</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, idx) => {
                const net = parseFloat(ln.net_amount) || 0;
                const vat = net * (parseFloat(ln.vat_rate) || 0) / 100;
                const cat = categories.find(c => c.id === ln.category_id);
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                    <td style={{ padding: 4 }}>
                      <select value={ln.category_id} onChange={e => updateLine(idx, 'category_id', e.target.value)} style={{ width: '100%' }}>
                        <option value="">-- seçiniz --</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.is_kkeg ? ' (KKEG)' : ''}{c.account_code ? ` [${c.account_code}]` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: 4 }}>
                      <input value={ln.description} onChange={e => updateLine(idx, 'description', e.target.value)} placeholder="opsiyonel" style={{ width: '100%' }} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input type="number" step="0.01" value={ln.net_amount}
                        onChange={e => updateLine(idx, 'net_amount', e.target.value)}
                        style={{ width: '100%', textAlign: 'right', fontFamily: 'monospace' }} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <select value={ln.vat_rate} onChange={e => updateLine(idx, 'vat_rate', e.target.value)} style={{ width: '100%' }}>
                        {VAT_RATES.map(v => <option key={v} value={v}>%{v}</option>)}
                      </select>
                    </td>
                    {isPusula && (
                      <td style={{ padding: 4 }}>
                        <input type="number" step="0.01" value={ln.withholding_rate}
                          onChange={e => updateLine(idx, 'withholding_rate', e.target.value)}
                          style={{ width: '100%', textAlign: 'right', fontFamily: 'monospace' }} placeholder="20" />
                      </td>
                    )}
                    <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                      {fmt(net + vat)}
                      {cat?.is_kkeg && <span style={{ marginLeft: 4, fontSize: '0.7rem', color: 'var(--warning)' }} title="Kanunen Kabul Edilmeyen Gider">⚠</span>}
                    </td>
                    <td style={{ padding: 4, textAlign: 'center' }}>
                      {lines.length > 1 && (
                        <button onClick={() => removeLine(idx)} title="Kalemi sil"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '1rem', padding: 4 }}>×</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toplamlar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 16px', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Net:</span>
          <span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{fmt(totals.net)}</span>
          <span style={{ color: 'var(--text-tertiary)' }}>KDV:</span>
          <span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{fmt(totals.vat)}</span>
          {totals.wh > 0 && <>
            <span style={{ color: 'var(--text-tertiary)' }}>Stopaj (-):</span>
            <span style={{ fontFamily: 'monospace', textAlign: 'right', color: 'var(--warning)' }}>-{fmt(totals.wh)}</span>
          </>}
          <span style={{ fontWeight: 700 }}>Brüt Toplam:</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, textAlign: 'right' }}>{fmt(totals.gross)}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ödenecek</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--danger)', fontFamily: 'monospace' }}>{fmt(totals.payable)} {header.currency_code || 'TRY'}</div>
        </div>
      </div>

      {/* Ödeme aracı */}
      <div style={{ borderTop: '2px solid var(--border-secondary)', paddingTop: 12 }}>
        <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 8 }}>Ödeme Aracı</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {PAY_METHODS.map(p => (
            <label key={p.value} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1px solid ' + (header.payment_method === p.value ? p.color : 'var(--border-secondary)'),
              background: header.payment_method === p.value ? p.color + '15' : 'var(--bg-input)',
              borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem',
            }}>
              <input type="radio" name="paym" checked={header.payment_method === p.value}
                onChange={() => { setHeader(prev => ({ ...prev, payment_method: p.value, payment_ref_id: '' })); }} />
              <span style={{ fontWeight: header.payment_method === p.value ? 600 : 400, color: header.payment_method === p.value ? p.color : 'inherit' }}>
                {p.label}
              </span>
            </label>
          ))}
        </div>
        <FormField label={payInstrumentLabel() + ` * (${cur})`}>
          <SearchableSelect
            value={header.payment_ref_id}
            onChange={v => setHeader(prev => ({ ...prev, payment_ref_id: v }))}
            options={payInstrumentOptions()}
            getLabel={o => o.code ? `${o.code} - ${o.name}` : o.name}
            getSubLabel={o => o.balance != null ? `${o.isCard ? 'Borç' : 'Bakiye'}: ${fmt(o.balance)} ${o.currency || cur}` : null}
            placeholder={`${payInstrumentLabel()} seçin…`}
          />
        </FormField>
        {payInstrumentOptions().length === 0 && header.payment_method !== 'personnel' && (
          <div style={{
            padding: '8px 12px', marginTop: -8, borderRadius: 8,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            color: 'var(--warning)', fontSize: '0.78rem',
          }}>
            ⚠ {cur} para biriminde aktif {payInstrumentLabel().toLowerCase()} bulunamadı. Fiş para birimini değiştirin veya ilgili hesabı tanımlayın.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── View Form ───────────────────────────────────────────────────────

function ViewForm({ voucher, categories }) {
  const docType = DOC_TYPES.find(d => d.value === voucher.document_type)?.label || voucher.document_type;
  const payMethod = PAY_METHODS.find(p => p.value === voucher.payment_method);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 10 }}>
        <Info label="Belge Türü" value={docType} />
        <Info label="Tarih" value={fmtDate(voucher.voucher_date)} />
        <Info label="Referans" value={voucher.reference_no || '-'} />
        <Info label="Cari" value={voucher.account_title || '-'} />
        <Info label="Ödeme" value={
          <>
            <span style={{ color: payMethod?.color, fontWeight: 600 }}>{payMethod?.label}</span>
            {voucher.payment_ref_name && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{voucher.payment_ref_name}</div>
            )}
          </>
        } />
        <Info label="Düzenleyen" value={voucher.created_by_name || '-'} />
      </div>
      {voucher.description && (
        <div style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem' }}>
          <strong>Açıklama:</strong> {voucher.description}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Kategori</th>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Açıklama</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 100 }}>Net</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 60 }}>KDV%</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 100 }}>KDV</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 110 }}>Toplam</th>
          </tr>
        </thead>
        <tbody>
          {(voucher.lines || []).map(l => (
            <tr key={l.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
              <td style={{ padding: 4 }}>
                {l.category_name}
                {l.is_kkeg && <span style={{ marginLeft: 4, fontSize: '0.68rem', color: 'var(--warning)' }} title="KKEG">⚠ KKEG</span>}
                {l.account_code && <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{l.account_code}</div>}
              </td>
              <td style={{ padding: 4 }}>{l.description || '-'}</td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(l.net_amount)}</td>
              <td style={{ padding: 4, textAlign: 'right' }}>%{l.vat_rate}</td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(l.vat_amount)}</td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(l.gross_amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--border-secondary)' }}>
            <td colSpan={2}></td>
            <td style={{ textAlign: 'right', padding: '8px 4px', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(voucher.total_net)}</td>
            <td></td>
            <td style={{ textAlign: 'right', padding: '8px 4px', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(voucher.total_vat)}</td>
            <td style={{ textAlign: 'right', padding: '8px 4px', fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem', color: 'var(--danger)' }}>
              {fmt(voucher.total_gross)} {voucher.currency_code}
            </td>
          </tr>
          {parseFloat(voucher.total_withholding) > 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'right', padding: '4px', fontSize: '0.78rem', color: 'var(--warning)' }}>Stopaj (-):</td>
              <td style={{ textAlign: 'right', padding: '4px', fontFamily: 'monospace', color: 'var(--warning)' }}>-{fmt(voucher.total_withholding)}</td>
            </tr>
          )}
          {parseFloat(voucher.total_withholding) > 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'right', padding: '4px', fontWeight: 700 }}>Ödenen:</td>
              <td style={{ textAlign: 'right', padding: '4px', fontFamily: 'monospace', fontWeight: 700 }}>
                {fmt(parseFloat(voucher.total_gross) - parseFloat(voucher.total_withholding))}
              </td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.88rem' }}>{value}</div>
    </div>
  );
}

